import React, { useCallback } from 'react';
import { Trash } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { ResponsiveDialog } from '@shared/components/ResponsiveDialog';
import { Box, ButtonV2, CancelButton, SpacingStack, TextV2 } from '@withjoy/joykit';
import { drawerOverrides } from '@shared/components/ResponsiveDialog/ResponsiveDialog.styles';
import { useNavigationPaneContext } from '@website-editor/context/NavigationPaneContext/NavigationPane.context';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useTranslation } from '@shared/core';
import { useCustomComponentStateContext } from '@website-editor/features/custom-page/context/CustomComponentStateContext/CustomComponentState.context';
import { useAccommodationsDelete } from '../hooks/useAccommodationsDelete';
import { useAdminAccommodationsTelemetry } from '../Accommodations.telemetry';
import { TravelMapBlockType } from '../Accommodations.types';
import { useAccommodationsContext } from '../context/useAccommodationsContext';

interface AccommodationsDeleteDialogProps {
  id?: string | null;
  pointOnMapId?: string | null;
  type: TravelMapBlockType;
  pageSlug: string;
  isSuperAdmin?: boolean;
  showCloseIcon?: boolean;
}

export const AccommodationsDeleteDialog: React.FC<AccommodationsDeleteDialogProps> = React.memo(props => {
  const { id, pointOnMapId, pageSlug, isSuperAdmin = false, showCloseIcon = false, type } = props;

  const { tq } = useTranslation('websiteEditor');
  const telemetry = useAdminAccommodationsTelemetry();
  const { navigateBack } = useNavigationPaneContext();
  const { accommodationsData } = useAccommodationsContext();
  const { showDeletePopup: isOpen, setCustomComponentBlockData, setShowDeletePopup, setRefetchQueries } = useCustomComponentStateContext();

  const handleCloseDeletePopup = useEventCallback(() => {
    telemetry.deleteItemDialogDismiss(pageSlug, { itemType: type, isSuperAdmin });
    setShowDeletePopup(false);
  });

  const getDeleteText = useCallback(() => {
    switch (type) {
      case TravelMapBlockType.travelMapAccommodationsCustom:
        return tq`Delete Custom Property`;
      case TravelMapBlockType.travelMapAccommodationsHotel:
        return tq`Delete Accommodation`;
      case TravelMapBlockType.travelMapAccommodationsRoomblock:
        return tq`Remove Stay from Page`;
      case TravelMapBlockType.travelMapVenue:
        return tq`Delete Venue`;
      default:
        return tq`Delete`;
    }
  }, [type, tq]);

  const handleOnDelete = () => {
    const hotel = accommodationsData?.accommodationPlacesV2?.find(i => i.id === id);
    telemetry.deleteItemSubmit(pageSlug, { itemType: type, isSuperAdmin, createdByType: hotel?.createdByType });
    if (type !== TravelMapBlockType.travelMapVenue) {
      setRefetchQueries(['AccommodationHotels']);
    }
    setShowDeletePopup(false);
    setCustomComponentBlockData({});
    navigateBack();
  };

  const { isLoading: deleting, onDelete } = useAccommodationsDelete({ handleCloseOnSuccess: handleOnDelete });

  const onDeleteItem = useEventCallback(() => onDelete(type, id, pointOnMapId));

  return (
    <ResponsiveDialog
      isOpen={isOpen}
      onClose={handleCloseDeletePopup}
      hideDivider
      removeBodyPadding
      hasCloseButton={showCloseIcon}
      drawerOverrides={{
        ...drawerOverrides,
        Content: {
          props: {
            ...drawerOverrides.Content?.props,
            boxShadow: '0px -7px 16px -8px rgba(0, 0, 0, 0.3), 0px -10px 27px -5px rgba(44, 41, 37, 0.25)',
          },
        },
        Body: { props: { ...drawerOverrides.Body?.props, paddingX: 6, paddingBottom: 8, paddingTop: showCloseIcon ? 0 : 8 } },
      }}
      dialogOverrides={{
        Content: { props: { width: ['100%', pxToRem(480)] } },
        Body: { props: { paddingX: 8, paddingY: 8 } },
        ...(showCloseIcon && { Header: { props: { paddingBottom: [0, 0] } } }),
      }}
    >
      <SpacingStack spacing={8}>
        <SpacingStack
          spacing={6}
          maxWidth={pxToRem(320)}
          marginX="auto"
        >
          <TextV2
            as="h3"
            typographyVariant="hed6"
            textAlign="center"
          >
            {tq`Are you sure you want to delete this item?`}
          </TextV2>
          <TextV2
            as="p"
            typographyVariant="body2"
            color="mono12"
            textAlign="center"
          >
            {tq`This cannot be undone.`}
          </TextV2>
        </SpacingStack>
        <Box width="100%">
          <ButtonV2
            intent="destructive"
            onClick={onDeleteItem}
            width="100%"
            startIcon={<Trash />}
            loading={deleting}
            disabled={deleting}
          >
            {getDeleteText()}
          </ButtonV2>
          <CancelButton
            marginRight="auto"
            onClick={handleCloseDeletePopup}
            width="100%"
            marginTop={5}
          >
            {tq`Cancel`}
          </CancelButton>
        </Box>
      </SpacingStack>
    </ResponsiveDialog>
  );
});
